import { TrendingUp, Users, BookOpen, Target, Award, Globe, MapPin, Calendar } from 'lucide-react';

const Impact = () => {
  const currentYear = 2024;

  const stats = [
    { icon: Users, label: 'Socios Activos', value: '12,458', change: '+23%', color: 'blue' },
    { icon: BookOpen, label: 'Cursos Completados', value: '8,742', change: '+35%', color: 'green' },
    { icon: Target, label: 'Campañas Lanzadas', value: '356', change: '+18%', color: 'purple' },
    { icon: Award, label: 'Badges Otorgados', value: '15,892', change: '+42%', color: 'orange' }
  ];

  const regions = [
    { name: 'España', socios: 4200, cursos: 3100, campanas: 142 },
    { name: 'México', socios: 3800, cursos: 2890, campanas: 98 },
    { name: 'Argentina', socios: 2100, cursos: 1560, campanas: 56 },
    { name: 'Colombia', socios: 1580, cursos: 1120, campanas: 38 },
    { name: 'Chile', socios: 780, cursos: 612, campanas: 22 }
  ];

  const achievements = [
    {
      title: 'Políticas Municipales Influenciadas',
      value: 47,
      description: 'Socios que lograron cambios en políticas locales de sostenibilidad'
    },
    {
      title: 'Proyectos Comunitarios Iniciados',
      value: 128,
      description: 'Iniciativas locales creadas por graduados de nuestros cursos'
    },
    {
      title: 'Horas de Voluntariado',
      value: '32,450',
      description: 'Horas dedicadas por socios a campañas de activismo'
    },
    {
      title: 'Personas Impactadas',
      value: '245,000+',
      description: 'Beneficiarios directos de las campañas lideradas por nuestros socios'
    }
  ];

  const topCourses = [
    { name: 'Liderazgo Transformacional', completions: 1245, rating: 4.8 },
    { name: 'Activismo Digital', completions: 1103, rating: 4.9 },
    { name: 'Gestión del Cambio', completions: 987, rating: 4.7 },
    { name: 'Comunicación para el Impacto', completions: 856, rating: 4.8 },
    { name: 'Sostenibilidad Urbana', completions: 743, rating: 4.6 }
  ];

  const topCampaigns = [
    { name: 'Ciudades Sin Plástico', participants: 2340, impact: 'Alto' },
    { name: 'Educación Financiera Comunitaria', participants: 1876, impact: 'Muy Alto' },
    { name: 'Espacios Verdes Urbanos', participants: 1654, impact: 'Alto' },
    { name: 'Justicia Climática Local', participants: 1432, impact: 'Medio' },
    { name: 'Transparencia Municipal', participants: 1287, impact: 'Alto' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-600 to-purple-600 text-white py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <TrendingUp className="w-16 h-16 mx-auto mb-6" />
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Nuestro Impacto {currentYear}
            </h1>
            <p className="text-xl md:text-2xl opacity-90">
              Transformando educación en acción cívica real y medible
            </p>
          </div>
        </div>
      </section>

      {/* Estadísticas Principales */}
      <section className="py-16 container mx-auto px-4">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto">
          {stats.map((stat, index) => {
            const Icon = stat.icon;
            const colorClasses = {
              blue: 'from-blue-400 to-blue-600',
              green: 'from-green-400 to-green-600',
              purple: 'from-purple-400 to-purple-600',
              orange: 'from-orange-400 to-orange-600'
            };
            
            return (
              <div key={index} className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-shadow">
                <div className={`w-12 h-12 bg-gradient-to-br ${colorClasses[stat.color as keyof typeof colorClasses]} rounded-lg flex items-center justify-center mb-4`}>
                  <Icon className="w-6 h-6 text-white" />
                </div>
                <p className="text-gray-600 text-sm mb-2">{stat.label}</p>
                <p className="text-3xl font-bold text-gray-900 mb-1">{stat.value}</p>
                <p className="text-green-600 text-sm font-semibold">{stat.change} vs {currentYear - 1}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Logros Destacados */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center text-gray-900 mb-12">
            Logros Destacados
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-7xl mx-auto">
            {achievements.map((achievement, index) => (
              <div key={index} className="text-center">
                <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-2xl p-8 mb-4">
                  <p className="text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600 mb-2">
                    {achievement.value}
                  </p>
                  <p className="text-lg font-semibold text-gray-900 mb-2">
                    {achievement.title}
                  </p>
                </div>
                <p className="text-sm text-gray-600">
                  {achievement.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Distribución Geográfica */}
      <section className="py-16 container mx-auto px-4">
        <h2 className="text-4xl font-bold text-center text-gray-900 mb-4">
          Distribución Geográfica
        </h2>
        <p className="text-center text-gray-600 text-lg mb-12">
          Presencia en 40 ciudades de habla hispana
        </p>
        <div className="max-w-5xl mx-auto">
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
            <table className="w-full">
              <thead className="bg-gradient-to-r from-blue-600 to-purple-600 text-white">
                <tr>
                  <th className="px-6 py-4 text-left">País/Región</th>
                  <th className="px-6 py-4 text-center">Socios</th>
                  <th className="px-6 py-4 text-center">Cursos Completados</th>
                  <th className="px-6 py-4 text-center">Campañas</th>
                </tr>
              </thead>
              <tbody>
                {regions.map((region, index) => (
                  <tr key={index} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="px-6 py-4 font-semibold text-gray-900 flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-blue-600" />
                      {region.name}
                    </td>
                    <td className="px-6 py-4 text-center text-gray-700">
                      {region.socios.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 text-center text-gray-700">
                      {region.cursos.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 text-center text-gray-700">
                      {region.campanas}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Top Cursos y Campañas */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 max-w-7xl mx-auto">
            {/* Top Cursos */}
            <div>
              <h3 className="text-3xl font-bold text-gray-900 mb-8 flex items-center gap-3">
                <BookOpen className="w-8 h-8 text-blue-600" />
                Top 5 Cursos
              </h3>
              <div className="space-y-4">
                {topCourses.map((course, index) => (
                  <div key={index} className="bg-white rounded-lg p-4 shadow-md hover:shadow-lg transition-shadow">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-semibold text-gray-900">
                        {index + 1}. {course.name}
                      </span>
                      <span className="text-sm bg-yellow-100 text-yellow-800 px-2 py-1 rounded">
                        ⭐ {course.rating}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600">
                      {course.completions.toLocaleString()} completaciones
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* Top Campañas */}
            <div>
              <h3 className="text-3xl font-bold text-gray-900 mb-8 flex items-center gap-3">
                <Target className="w-8 h-8 text-purple-600" />
                Top 5 Campañas
              </h3>
              <div className="space-y-4">
                {topCampaigns.map((campaign, index) => (
                  <div key={index} className="bg-white rounded-lg p-4 shadow-md hover:shadow-lg transition-shadow">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-semibold text-gray-900">
                        {index + 1}. {campaign.name}
                      </span>
                      <span className={`text-xs px-2 py-1 rounded font-medium ${
                        campaign.impact === 'Muy Alto' ? 'bg-green-100 text-green-800' :
                        campaign.impact === 'Alto' ? 'bg-blue-100 text-blue-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {campaign.impact}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600">
                      {campaign.participants.toLocaleString()} participantes
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Timeline de Crecimiento */}
      <section className="py-16 container mx-auto px-4">
        <h2 className="text-4xl font-bold text-center text-gray-900 mb-12">
          Crecimiento Año tras Año
        </h2>
        <div className="max-w-4xl mx-auto">
          <div className="space-y-8">
            {[
              { year: 2024, socios: '12,458', cursos: '85', campanas: '356', highlight: 'Año actual' },
              { year: 2023, socios: '10,120', cursos: '73', campanas: '302', highlight: 'Expansión internacional' },
              { year: 2022, socios: '7,450', cursos: '58', campanas: '218', highlight: 'App móvil lanzada' },
              { year: 2021, socios: '4,200', cursos: '35', campanas: '127', highlight: 'Primera campaña ganada' },
              { year: 2020, socios: '1,500', cursos: '10', campanas: '5', highlight: 'Fundación' }
            ].map((item, index) => (
              <div key={index} className="flex items-center gap-6 bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow">
                <div className="flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-500 rounded-full text-white font-bold text-lg flex-shrink-0">
                  <Calendar className="w-8 h-8" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h4 className="text-2xl font-bold text-gray-900">{item.year}</h4>
                    <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-semibold">
                      {item.highlight}
                    </span>
                  </div>
                  <div className="flex gap-6 text-sm text-gray-600">
                    <span><strong>{item.socios}</strong> socios</span>
                    <span><strong>{item.cursos}</strong> cursos</span>
                    <span><strong>{item.campanas}</strong> campañas</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-16 bg-gradient-to-r from-blue-600 to-purple-600 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <Globe className="w-16 h-16 mx-auto mb-6" />
            <h2 className="text-4xl font-bold mb-4">
              Sé Parte del Cambio
            </h2>
            <p className="text-xl mb-8 opacity-90">
              Únete a miles de agentes de cambio que están transformando sus comunidades
            </p>
            <a
              href="/registro"
              className="inline-block px-10 py-4 bg-white text-blue-600 rounded-lg font-semibold text-lg hover:bg-gray-100 transition-all shadow-xl"
            >
              Únete Gratis Ahora
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Impact;
